import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { UserPlus } from 'lucide-react';
import { mensajeDeError } from '../lib/mensajeDeError';

// DAR DE ALTA A UN CLIENTE DESDE EL PANEL (Jesús, reunión del 25-09: «te voy a dar el email
// y le haces accesible; una cuenta de cliente normal»). Nombre, correo, plan y hasta cuándo
// tiene acceso (o de cortesía). La contraseña es temporal y sale UNA vez, como al
// restablecerla: se copia y se le pasa por WhatsApp.
const DarDeAlta = ({ onCreado }) => {
    const { api, planCatalog } = useAuth();
    const navigate = useNavigate();
    const [abierto, setAbierto] = useState(false);
    const [form, setForm] = useState({ nombre: '', email: '', plan: '', hasta: '', cortesia: false });
    const [guardando, setGuardando] = useState(false);
    const [hecho, setHecho] = useState(null);
    const planes = Object.values(planCatalog || {}).filter(p => p.asignable);
    const campo = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
    const listo = form.email.trim() && form.plan && (form.cortesia || form.hasta);

    const cerrar = () => {
        setAbierto(false);
        setHecho(null);
        setForm({ nombre: '', email: '', plan: '', hasta: '', cortesia: false });
    };

    const crear = async () => {
        setGuardando(true);
        try {
            const r = await api.post('/admin/clients/alta', form);
            setHecho({ ...r.data, nombre: form.nombre.trim() || r.data.email });
            onCreado && onCreado();
        } catch (err) {
            toast.error(mensajeDeError(err, 'No se pudo dar de alta'));
        } finally {
            setGuardando(false);
        }
    };

    const copiar = () => navigator.clipboard.writeText(hecho.temp_password)
        .then(() => toast.success('Contraseña copiada')).catch(() => {});

    const caja = 'w-full bg-[#0A0A0A] border border-[#333] text-white text-sm rounded-lg px-2 py-2 mt-1';
    return (
        <>
            <Button onClick={() => setAbierto(true)} data-testid="dar-de-alta"
                className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white shrink-0">
                <UserPlus className="w-4 h-4 mr-2" /> Dar de alta
            </Button>
            <Dialog open={abierto} onOpenChange={(o) => !o && cerrar()}>
                <DialogContent className="bg-[#111] border-[#333] max-w-md text-white" data-testid="dar-de-alta-dialogo">
                    <DialogHeader>
                        <DialogTitle className="uppercase tracking-wider flex items-center gap-2">
                            <UserPlus className="w-5 h-5 text-[#FF671F]" /> {hecho ? 'Cliente dado de alta' : 'Dar de alta un cliente'}
                        </DialogTitle>
                    </DialogHeader>
                    {!hecho ? (
                        <div className="space-y-3">
                            <label className="block text-white/60 text-xs">Nombre
                                <Input value={form.nombre} onChange={campo('nombre')} data-testid="alta-nombre"
                                    className="bg-[#0A0A0A] border-[#333] text-white mt-1" />
                            </label>
                            <label className="block text-white/60 text-xs">Correo
                                <Input type="email" value={form.email} onChange={campo('email')} data-testid="alta-email"
                                    className="bg-[#0A0A0A] border-[#333] text-white mt-1" />
                            </label>
                            <label className="block text-white/60 text-xs">Plan
                                <select value={form.plan} onChange={campo('plan')} className={caja} data-testid="alta-plan">
                                    <option value="">Elige el plan</option>
                                    {['activo', 'especial'].map(estado => {
                                        const grupo = planes.filter(p => p.estado === estado);
                                        if (!grupo.length) return null;
                                        return (
                                            <optgroup key={estado} label={{ activo: 'Activos', especial: 'Especiales' }[estado]}>
                                                {grupo.map(p => <option key={p.code} value={p.code}>{p.name}</option>)}
                                            </optgroup>
                                        );
                                    })}
                                    {planes.some(p => p.estado !== 'activo' && p.estado !== 'especial') && (
                                        <optgroup label="Planes antiguos">
                                            {planes.filter(p => p.estado !== 'activo' && p.estado !== 'especial')
                                                .map(p => <option key={p.code} value={p.code}>{p.name}</option>)}
                                        </optgroup>
                                    )}
                                </select>
                            </label>
                            <label className="block text-white/60 text-xs">Acceso hasta
                                <input type="date" value={form.hasta} onChange={campo('hasta')} disabled={form.cortesia}
                                    className={`${caja} disabled:opacity-40`} data-testid="alta-hasta" />
                            </label>
                            <label className="flex items-center gap-2 text-white/80 text-sm">
                                <input type="checkbox" checked={form.cortesia} onChange={campo('cortesia')} data-testid="alta-cortesia" />
                                De cortesía, sin fecha de fin
                            </label>
                            <p className="text-white/40 text-xs">El plan arranca el lunes, como cuando se asigna desde la ficha.</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <p className="text-white/60 text-sm">Contraseña temporal de <span className="text-white font-semibold">{hecho.nombre}</span> ({hecho.email}):</p>
                            <div className="bg-[#0A0A0A] border border-[#333] rounded-xl p-4 text-center">
                                <span className="text-[#FF671F] font-mono font-bold text-lg" data-testid="alta-temp-password">{hecho.temp_password}</span>
                            </div>
                            <p className="text-yellow-400/80 text-xs">Solo se muestra ahora: cópiala y pásasela por WhatsApp. Recomiéndale cambiarla al entrar (Perfil, Cambiar contraseña).</p>
                        </div>
                    )}
                    <DialogFooter className="gap-2">
                        {!hecho ? (
                            <>
                                <Button variant="outline" onClick={cerrar} className="bg-transparent border-[#333] text-white">Cancelar</Button>
                                <Button onClick={crear} disabled={!listo || guardando} data-testid="alta-crear"
                                    className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white">
                                    {guardando ? 'Dando de alta…' : 'Dar de alta'}
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button onClick={copiar} className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white">Copiar</Button>
                                <Button variant="outline" onClick={() => { const id = hecho.client_id; cerrar(); navigate(`/admin/clients/${id}`); }}
                                    className="bg-transparent border-[#333] text-white" data-testid="alta-ver-ficha">Ver su ficha</Button>
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default DarDeAlta;
